import { useLostConnectionListener } from '@liveblocks/react'
import { ClientSideSuspense } from '@liveblocks/react/suspense'
import type { MemoNote } from '@seen/core/models/memoNote'
import { type ReactNode, useCallback, useEffect, useState } from 'react'
import { CollaborativeEditor } from '#src/components/CollaborativeEditor.tsx'
import { RichTextEditor } from '#src/components/rich-text-editor/RichTextEditor.tsx'
import { useDebouncedCallback } from '#src/hooks/useDebouncedCallback.ts'
import { usePatchMutation } from '#src/hooks/usePatchMutation.ts'

export function MemoEditor({ memo }: { memo: MemoNote }): ReactNode {
  return (
    <ClientSideSuspense fallback={<FallbackEditor memo={memo} />}>
      <ConnectedMemoEditor memo={memo} />
    </ClientSideSuspense>
  )
}

function ConnectedMemoEditor({ memo }: { memo: MemoNote }): ReactNode {
  const [isDisconnected, setIsDisconnected] = useState(false)
  const [debugDisconnected, setDebugDisconnected] = useState(false)

  const handleLostConnection = useCallback((event: string) => {
    if (event === 'lost' || event === 'failed') {
      setIsDisconnected(true)
    } else if (event === 'restored') {
      setIsDisconnected(false)
    }
  }, [])

  useLostConnectionListener(handleLostConnection)

  useEffect(() => {
    if (process.env.NODE_ENV === 'production') {
      return
    }
    ;(window as any).__memoDebug = {
      setDisconnected: setDebugDisconnected,
    }
    return () => {
      delete (window as any).__memoDebug
    }
  }, [])

  const showFallback = isDisconnected || debugDisconnected

  const { mutate: patchMemo } = usePatchMutation<MemoNote>()

  const handleChange = useCallback(
    (html: string) => {
      patchMemo({
        instance: memo,
        mutateFn: (instance) => {
          instance.content = html
        },
      })
    },
    [patchMemo, memo]
  )

  if (showFallback) {
    return (
      <div className="flex min-h-0 flex-1 flex-col">
        <DisconnectedBadge />
        <FallbackEditor memo={memo} />
      </div>
    )
  }

  return (
    <CollaborativeEditor
      field="memo"
      initialContent={memo.content}
      onChange={handleChange}
    />
  )
}

function FallbackEditor({ memo }: { memo: MemoNote }): ReactNode {
  const patchMutation = usePatchMutation<MemoNote>()

  const debouncedSave = useDebouncedCallback((html: string) => {
    patchMutation.mutate({
      instance: memo,
      mutateFn: (instance) => {
        instance.content = html
      },
    })
  }, 2500)

  useEffect(() => () => debouncedSave.flush(), [debouncedSave])

  return (
    <RichTextEditor
      value={memo.content}
      onChange={debouncedSave}
      variant="unstyled"
      placeholder="Type anything…"
    />
  )
}

function DisconnectedBadge(): ReactNode {
  return (
    <div className="flex items-center gap-1.5 px-1 py-1 text-xs text-foreground-secondary">
      <span className="size-2 rounded-full bg-yellow-400" />
      Multiplayer unavailable
    </div>
  )
}
